import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useSearchParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import ChatList from "./components/ChatList/ChatList";
import ConversationView from "./components/ConversationView/ConversationView";
import "./chat-theme.css";
import "./ChatContainer.css";
import apiService from "../../service/apiservice/apiService";
import { useReadMemory } from "../generic/hooks/useReadMemory";
import MemoryType from "../../service/memory/enum/MemoryType";
import { useGetOrgIdentifier } from "../generic/hooks/useGetOrgIdentifier";
import {
  chatCommunicationPageActiveState,
  chatComposerTextState,
  chatCurrentUserIdState,
  chatEditingMessageIdState,
  chatRoomsState,
  selectedChatRoomIdState,
  sortedChatRoomsState,
} from "./state/chatState";
import { getChatRoomId } from "./chatRoomUtils";
import useActiveRoomSubscription from "./hooks/useActiveRoomSubscription";

const CHAT_MOBILE_BREAKPOINT_PX = 768;

const useChatMobileLayout = () => {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${CHAT_MOBILE_BREAKPOINT_PX}px)`).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${CHAT_MOBILE_BREAKPOINT_PX}px)`,
    );
    const updateLayout = () => setIsMobile(mediaQuery.matches);
    updateLayout();
    mediaQuery.addEventListener("change", updateLayout);
    return () => mediaQuery.removeEventListener("change", updateLayout);
  }, []);

  return isMobile;
};

const ChatContainer = () => {
  const user = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const [searchParams] = useSearchParams();
  const roomIdFromUrl = searchParams.get("roomId");
  const setChatRooms = useSetRecoilState(chatRoomsState);
  const setCurrentUserId = useSetRecoilState(chatCurrentUserIdState);
  const [selectedRoomId, setSelectedRoomId] = useRecoilState(selectedChatRoomIdState);
  const setComposerText = useSetRecoilState(chatComposerTextState);
  const setEditingMessageId = useSetRecoilState(chatEditingMessageIdState);
  const setCommunicationPageActive = useSetRecoilState(
    chatCommunicationPageActiveState,
  );
  const sortedRooms = useRecoilValue(sortedChatRoomsState);
  const isMobileLayout = useChatMobileLayout();
  const showMobileConversation = isMobileLayout && Boolean(selectedRoomId);
  const [isLoadingRooms, setIsLoadingRooms] = useState(
    () => Boolean(user?.userIdentifier && organizationIdentifier),
  );

  // Room-level STOMP subscription only — global socket lives in SocketProvider.
  useActiveRoomSubscription();

  useEffect(() => {
    setCommunicationPageActive(true);
    return () => setCommunicationPageActive(false);
  }, [setCommunicationPageActive]);

  useEffect(() => {
    setCurrentUserId(user?.userIdentifier || null);
  }, [setCurrentUserId, user?.userIdentifier]);

  useEffect(() => {
    let isCancelled = false;

    const loadRooms = async () => {
      if (!user?.userIdentifier || !organizationIdentifier) {
        setChatRooms([]);
        if (!isCancelled) {
          setIsLoadingRooms(false);
        }
        return;
      }

      if (!isCancelled) {
        setIsLoadingRooms(true);
      }

      try {
        const response = await apiService.get(
          "niramayafoundation/api/v1/communication/rooms",
          {
            recipient: user?.userIdentifier,
            organizationIdentifier,
          },
        );

        if (isCancelled) {
          return;
        }

        if (Array.isArray(response)) {
          setChatRooms(response);
        } else {
          setChatRooms([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingRooms(false);
        }
      }
    };

    loadRooms();

    return () => {
      isCancelled = true;
    };
  }, [organizationIdentifier, setChatRooms, user?.userIdentifier]);

  useEffect(() => {
    if (!sortedRooms.length) {
      setSelectedRoomId(null);
      return;
    }

    if (roomIdFromUrl) {
      const matchingRoom = sortedRooms.find(
        (room) => getChatRoomId(room) === String(roomIdFromUrl),
      );

      if (matchingRoom) {
        const matchingRoomId = getChatRoomId(matchingRoom);
        if (selectedRoomId !== matchingRoomId) {
          setSelectedRoomId(matchingRoomId);
        }
        return;
      }
    }

    const roomStillExists = sortedRooms.some(
      (room) => getChatRoomId(room) === String(selectedRoomId),
    );

    if (!roomStillExists) {
      const shouldAutoSelectFirstRoom = !isMobileLayout || Boolean(roomIdFromUrl);
      setSelectedRoomId(
        shouldAutoSelectFirstRoom ? getChatRoomId(sortedRooms[0]) : null,
      );
    }
  }, [
    isMobileLayout,
    roomIdFromUrl,
    selectedRoomId,
    setSelectedRoomId,
    sortedRooms,
  ]);

  useEffect(() => {
    setComposerText("");
    setEditingMessageId(null);
  }, [selectedRoomId, setComposerText, setEditingMessageId]);

  return (
    <div className="chat-page-root">
      {isLoadingRooms ? (
        <div className="chat-loader-container" role="status" aria-live="polite" aria-busy="true">
          <Spin size="large" className="spinnerCenter" />
        </div>
      ) : (
        <div
          className={`chat-main-container${showMobileConversation ? " chat-show-conversation" : ""}`}
        >
          <aside className="chat-sidebar">
            <ChatList />
          </aside>
          <main className="chat-content">
            <ConversationView />
          </main>
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
